import { omit } from 'lodash';
import moment from 'moment';
import { ErrorMessage, MessageProps, SuccessMessage } from 'framework/ui/Message';
import { DATE_FORMAT } from 'framework/utils';
import { BusinessOutcomesType } from 'domain/projects/newProject/businessOutcomes/types';
import { MilestonesType } from 'domain/projects/newProject/milestones/types';
import { RisksType, OUTCOME_STATUS, MILESTONE_STATUS, RISK_STATUS } from 'domain/shared/newProject';
import { StakeholderSearchResultItem } from 'domain/projects/newProject/stakeholders/types';
import { ASSUMPTION_STATUS, AssumptionType } from 'domain/projects/newProject/assumptions/types';
import { MILESTONES_ALIAS } from 'domain/projects/newProject/milestones/useMilestonesService';
import {
  reuseGroups,
  ReuseCandidateItem,
  ReuseGroup,
  ReuseGroupConfig,
  ReuseSelection,
  SelectableProject,
  TransferResult,
} from './types';

export const TRANSFER_SUCCESS_CONTEXT = 'reuse-data:success';
export const TRANSFER_ERROR_CONTEXT = 'reuse-data:error';

const stripTypename = <T extends object>(item: T): T => omit(item, '__typename') as T;

const isTransferableOutcome = (item: { status?: string }) => item.status === OUTCOME_STATUS.ACTIVE;
const isTransferableMilestone = (item: { status?: string; type?: string }) =>
  item.status === MILESTONE_STATUS.ACTIVE && item.type === MILESTONES_ALIAS.NON_BILLING_MILESTONE;
const isTransferableRisk = (item: { status?: string }) => item.status === RISK_STATUS.ACTIVE;
const isTransferableAssumption = (item: { status?: string }) => item.status !== ASSUMPTION_STATUS.INACTIVE;

export const getTransferableOutcomes = (items?: BusinessOutcomesType[]): BusinessOutcomesType[] =>
  (items ?? []).filter(isTransferableOutcome).map(({ milestones, projectId, ...rest }) => stripTypename(rest));

export const getTransferableMilestones = (items?: MilestonesType[]): MilestonesType[] =>
  (items ?? []).filter(isTransferableMilestone).map(item => {
    const {
      initialPlannedDate,
      currentPlannedDate,
      completionDate,
      businessOutcomes,
      rejectionReason,
      rejectionDate,
      comment,
      ...rest
    } = item;
    return stripTypename(rest);
  });

export const getTransferableRisks = (items?: RisksType[]): RisksType[] =>
  (items ?? []).filter(isTransferableRisk).map(stripTypename);

const getTransferableStakeholders = (items?: StakeholderSearchResultItem[]): StakeholderSearchResultItem[] =>
  (items ?? []).map((item, idx) => ({ ...stripTypename(item), alias: String(idx + 1) }) as StakeholderSearchResultItem);

const getTransferableAssumptions = (items?: AssumptionType[]): AssumptionType[] =>
  (items ?? []).filter(isTransferableAssumption).map(stripTypename);

const isNotMarkedForDeletion = (item: object) => !(item as Record<string, unknown>)['_isMarkedForDeletion'];

const mapOutcomesForCreate = (items: BusinessOutcomesType[] = []): BusinessOutcomesType[] =>
  items
    .filter(isNotMarkedForDeletion)
    .map(item => ({ ...item, id: null, alias: null }) as unknown as BusinessOutcomesType);

const mapMilestonesForCreate = (items: MilestonesType[] = []): MilestonesType[] =>
  items
    .filter(isNotMarkedForDeletion)
    .map(
      item => omit({ ...item, id: null, alias: null }, 'hasInitialPlannedDateFromBackend') as unknown as MilestonesType,
    );

export const mapRisksForCreate = (items: RisksType[] = [], riskOwner?: string, userAlias?: string): RisksType[] => {
  return items.filter(isNotMarkedForDeletion).map(
    item =>
      ({
        alias: null,
        createdAt: moment().format(DATE_FORMAT.API_ISO),
        createdBy: userAlias,
        description: item.description,
        id: null,
        mitigationStrategy: item.mitigationStrategy,
        probability: item.probability,
        severity: item.severity,
        title: item.title,
        type: item.type,
        status: item.status,
        isIncludedInCustomerReport: item.isIncludedInCustomerReport,
        riskOwner,
      }) as unknown as RisksType,
  );
};

const mapStakeholdersForCreate = (items: StakeholderSearchResultItem[] = []): StakeholderSearchResultItem[] =>
  items.filter(isNotMarkedForDeletion).map(item => {
    const { alias, ...rest } = item as StakeholderSearchResultItem & { alias?: string };
    return { ...rest, id: null } as unknown as StakeholderSearchResultItem;
  });

const mapAssumptionsForCreate = (items: AssumptionType[] = []) =>
  items.filter(isNotMarkedForDeletion).map(item => ({
    description: item.description,
    assignedRole: item.assignedRole,
    status: ASSUMPTION_STATUS.VALID,
  }));

export const runTransferTasks = async (tasks: Record<string, Promise<unknown>>): Promise<TransferResult> => {
  const labels = Object.keys(tasks);
  const settled = await Promise.allSettled(Object.values(tasks));

  return {
    succeeded: labels.filter((_, i) => settled[i].status === 'fulfilled'),
    failed: labels.filter((_, i) => settled[i].status === 'rejected'),
  };
};

export const buildTransferSuccessMessage = (succeeded: string[]): MessageProps | null =>
  succeeded.length ? new SuccessMessage(`Successfully transferred: ${succeeded.join(', ')}.`) : null;

export const buildTransferErrorMessage = (failed: string[]): MessageProps | null =>
  failed.length
    ? new ErrorMessage(`Failed to transfer: ${failed.join(', ')}. Please add the failed items manually in their tabs.`)
    : null;

export const reuseGroupConfig: Record<ReuseGroup, ReuseGroupConfig> = {
  businessOutcomes: {
    label: 'Business outcomes',
    isTransferable: isTransferableOutcome,
    extract: data => getTransferableOutcomes(data?.businessOutcomes),
    mapForCreate: items => mapOutcomesForCreate(items as BusinessOutcomesType[]),
  },
  milestones: {
    label: 'Milestones',
    isTransferable: isTransferableMilestone,
    extract: data => getTransferableMilestones(data?.milestones),
    mapForCreate: items => mapMilestonesForCreate(items as MilestonesType[]),
  },
  risks: {
    label: 'Risks',
    isTransferable: isTransferableRisk,
    extract: data => getTransferableRisks(data?.risks),
    mapForCreate: (items, { projectManager, userAlias }) =>
      mapRisksForCreate(items as RisksType[], projectManager, userAlias),
  },
  stakeholders: {
    label: 'Stakeholders',
    isTransferable: () => true,
    extract: data => getTransferableStakeholders(data?.stakeholders),
    mapForCreate: items => mapStakeholdersForCreate(items as StakeholderSearchResultItem[]),
  },
  assumptions: {
    label: 'Assumptions',
    isTransferable: isTransferableAssumption,
    extract: data => getTransferableAssumptions(data?.assumptions),
    mapForCreate: items => mapAssumptionsForCreate(items as AssumptionType[]),
  },
};

export const buildSearchString = (codeName: string, accountId: string | number) =>
  JSON.stringify({ filter: { name: { eq: codeName }, accountId: { eq: accountId } } });

const groupHasData = (project: SelectableProject, group: ReuseGroup) => (project[group]?.length ?? 0) > 0;

const groupHasTransferable = (project: SelectableProject, group: ReuseGroup) =>
  ((project[group] ?? []) as ReuseCandidateItem[]).some(reuseGroupConfig[group].isTransferable);

const EMPTY_SELECTION = {
  predecessor: null,
  transferableGroups: [] as ReuseGroup[],
  hasTransferableData: false,
  isFirstReuse: false,
};

export const selectReuseSource = <T extends SelectableProject>(
  baseProjects: T[] | undefined,
  projectId: string | undefined,
): ReuseSelection<T> => {
  if (!baseProjects || !projectId) return EMPTY_SELECTION;

  const current = baseProjects.find(p => p.id === projectId);
  const currentCreatedAt = current?.health?.createdAt;
  if (!current || !currentCreatedAt) return EMPTY_SELECTION;

  const siblings = baseProjects.filter(p => p.id !== projectId);
  const isCurrentLatest = siblings.every(p => p.health?.createdAt && p.health.createdAt < currentCreatedAt);
  if (!isCurrentLatest) return EMPTY_SELECTION;

  const predecessor = siblings
    .filter(p => p.health?.createdAt && p.health.createdAt < currentCreatedAt)
    .sort((a, b) => (b.health?.createdAt ?? '').localeCompare(a.health?.createdAt ?? ''))[0];
  if (!predecessor) return EMPTY_SELECTION;

  const transferableGroups = reuseGroups.filter(
    group => !groupHasData(current, group) && groupHasData(predecessor, group),
  );
  const hasTransferableData = transferableGroups.some(group => groupHasTransferable(predecessor, group));
  const isFirstReuse = reuseGroups.every(group => !groupHasData(current, group));

  return { predecessor, transferableGroups, hasTransferableData, isFirstReuse };
};
